import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CreditCard, Loader2, Smartphone, XCircle } from 'lucide-react';
import * as api from '../services/api';

const JQUERY_URL = 'https://code.jquery.com/jquery-3.7.1.min.js';

const getScriptCache = () => {
  if (!window.__journiScriptLoads) {
    window.__journiScriptLoads = {};
  }
  return window.__journiScriptLoads;
};

const loadScriptOnce = (cacheKey, src) => {
  const cache = getScriptCache();
  if (cache[cacheKey]) {
    return cache[cacheKey];
  }

  cache[cacheKey] = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-journi-script="${cacheKey}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error(`Failed to load script: ${src}`)), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.journiScript = cacheKey;
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
      resolve();
    }, { once: true });
    script.addEventListener('error', () => reject(new Error(`Failed to load script: ${src}`)), { once: true });
    document.body.appendChild(script);
  });

  return cache[cacheKey];
};

const parseMessagePayload = (raw) => {
  let data = raw;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch (error) {
      return null;
    }
  }
  if (!data || typeof data !== 'object') {
    return null;
  }
  if (data.payload && typeof data.payload === 'object') {
    data = { ...data.payload, ...data };
  }
  if (!data.pp_TxnRefNo && !data.pp_ResponseCode && !data.ResponseCode && !data.pp_SecureHash) {
    return null;
  }
  return data;
};

const allowJazzCashOrigin = (origin) => {
  if (!origin) {
    return true;
  }
  return origin === window.location.origin || /(^https:\/\/|^http:\/\/).*(jazzcash\.com\.pk)$/i.test(origin);
};

const upsertFormFields = (form, fields) => {
  Object.entries(fields).forEach(([name, value]) => {
    let input = form.querySelector(`[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value == null ? '' : String(value);
  });
};

const submitReturnForm = (form, payload) => {
  upsertFormFields(form, payload);
  form.submit();
};

export default function JazzCashCheckoutModal({
  isOpen,
  onClose,
  checkoutContext,
}) {
  const formRef = useRef(null);
  const [paymentMethod, setPaymentMethod] = useState('wallet');
  const [isPreparing, setIsPreparing] = useState(false);
  const [widgetReady, setWidgetReady] = useState(false);
  const [widgetError, setWidgetError] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [checkoutSession, setCheckoutSession] = useState(null);

  const actionLabel = useMemo(() => {
    if (paymentMethod === 'card') {
      return 'Continue with Card';
    }
    return 'Continue with Wallet';
  }, [paymentMethod]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleMessage = (event) => {
      if (!allowJazzCashOrigin(event.origin)) {
        return;
      }

      const payload = parseMessagePayload(event.data);
      if (!payload || !formRef.current || !checkoutSession) {
        return;
      }

      const mergedPayload = {
        ...checkoutSession.payload,
        ...payload,
      };
      setStatusNote('JazzCash returned a result. Finalizing payment...');
      submitReturnForm(formRef.current, mergedPayload);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isOpen, checkoutSession]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setPaymentMethod(checkoutContext?.defaultMethod || 'wallet');
    setIsPreparing(false);
    setWidgetReady(false);
    setWidgetError('');
    setStatusNote('');
    setCheckoutSession(null);
  }, [isOpen, checkoutContext]);

  if (!isOpen || !checkoutContext) {
    return null;
  }

  const loadHostedCheckout = async () => {
    setIsPreparing(true);
    setWidgetError('');
    setStatusNote('');

    try {
      let response;
      switch (checkoutContext.scope) {
        case 'recurring':
          response = await api.initiateJazzCashRecurring(checkoutContext.rideRequestIds || [], paymentMethod);
          break;
        case 'trust_deposit':
          response = await api.initiateJazzCashDeposit(paymentMethod);
          break;
        case 'subscription':
          response = await api.initiateJazzCashSubscription(checkoutContext.plan, paymentMethod);
          break;
        case 'token_topup':
          response = await api.initiateJazzCashTopup(checkoutContext.tokensCount, paymentMethod);
          break;
        case 'booking':
        default:
          response = await api.initiateJazzCashBooking(checkoutContext.rideRequestId, paymentMethod);
          break;
      }

      if (!response.success || !response.payload) {
        throw new Error(response.message || 'JazzCash payload could not be prepared.');
      }

      setCheckoutSession(response);

      if (!formRef.current) {
        throw new Error('JazzCash form is not available.');
      }

      // Clear any previous inputs inside `#JazzCashFields` to ensure a clean slate
      const fieldsContainer = formRef.current.querySelector('#JazzCashFields');
      if (fieldsContainer) {
        fieldsContainer.innerHTML = '';
      }

      upsertFormFields(formRef.current, response.payload);

      if (paymentMethod === 'card') {
        if (!response.hosted_script_url) {
          throw new Error('JazzCash card script URL not provided.');
        }

        await loadScriptOnce('jquery', JQUERY_URL);
        await loadScriptOnce(response.hosted_script_url, response.hosted_script_url);

        // Wrap renderPayaxisFields to make it run-once and avoid duplicate rendering race conditions
        if (typeof window.renderPayaxisFields === 'function' && !window.renderPayaxisFields.isWrapped) {
          const originalRender = window.renderPayaxisFields;
          window.renderPayaxisFields = function() {
            const container = document.getElementById('JazzCashFields');
            if (container && container.querySelector('#pp_CustomerCardNumber')) {
              return; // Already rendered
            }
            originalRender();
          };
          window.renderPayaxisFields.isWrapped = true;
        }

        if (typeof window.renderPayaxisFields === 'function') {
          window.renderPayaxisFields();
        }

        if (typeof window.populateJazzCashFields === 'function') {
          window.populateJazzCashFields(response.payload);
        }
      }

      setWidgetReady(true);
      setStatusNote(
        paymentMethod === 'card'
          ? 'JazzCash checkout is ready below.'
          : 'JazzCash wallet checkout is ready. Click below to proceed.'
      );
    } catch (error) {
      console.error('JazzCash widget failed to load:', error);
      setWidgetError(error.message || 'JazzCash checkout could not be loaded.');
      setWidgetReady(false);
    } finally {
      setIsPreparing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] bg-gray-950/60 backdrop-blur-sm px-4 py-6 overflow-y-auto">
      <style>{`
        #JazzCashFields {
          display: grid !important;
          grid-template-columns: 1fr 1fr !important;
          gap: 16px !important;
          margin-top: 8px !important;
        }
        #JazzCashFields br {
          display: none !important;
        }
        #JazzCashFields label {
          font-size: 0.75rem !important;
          font-weight: 600 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.05em !important;
          color: #4b5563 !important;
          margin-bottom: -10px !important;
          display: block !important;
        }
        #JazzCashFields label:nth-of-type(1) { grid-column: span 2 !important; }
        #pp_CustomerCardNumber { grid-column: span 2 !important; }
        
        #JazzCashFields label:nth-of-type(2) { grid-column: span 1 !important; }
        #pp_CustomerCardExpiryM { grid-column: span 1 !important; }
        
        #JazzCashFields label:nth-of-type(3) { grid-column: span 1 !important; }
        #pp_CustomerCardExpiryY { grid-column: span 1 !important; }
        
        #JazzCashFields label:nth-of-type(4) { grid-column: span 2 !important; }
        #pp_CustomerCardCvv { grid-column: span 2 !important; }

        #JazzCashFields input[type="text"],
        #JazzCashFields input[type="password"],
        #JazzCashFields select {
          width: 100% !important;
          padding: 10px 14px !important;
          border-radius: 8px !important;
          border: 1px solid #d1d5db !important;
          background-color: #ffffff !important;
          font-size: 0.875rem !important;
          color: #1f2937 !important;
          outline: none !important;
          transition: all 0.2s !important;
        }
        #JazzCashFields input[type="text"]:focus,
        #JazzCashFields input[type="password"]:focus,
        #JazzCashFields select:focus {
          border-color: #059669 !important;
          box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.15) !important;
        }
      `}</style>

      <div className="mx-auto max-w-2xl rounded-2xl bg-white shadow-2xl border border-gray-200">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-600">JazzCash Checkout</div>
            <h3 className="mt-1 text-xl font-semibold text-gray-900">{checkoutContext.title}</h3>
            {checkoutContext.description ? (
              <p className="mt-1 text-sm text-gray-600">{checkoutContext.description}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-200 p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
          >
            <XCircle className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 px-6 py-5">
          <div className="grid gap-3 md:grid-cols-2">
            <button
              type="button"
              onClick={() => {
                if (paymentMethod !== 'wallet') {
                  setPaymentMethod('wallet');
                  setWidgetReady(false);
                  setCheckoutSession(null);
                  setWidgetError('');
                  setStatusNote('');
                }
              }}
              className={`rounded-xl border px-4 py-4 text-left transition ${
                paymentMethod === 'wallet'
                  ? 'border-emerald-500 bg-emerald-50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <Smartphone className="h-4 w-4 text-emerald-600" />
                Mobile Wallet
              </div>
              <div className="mt-1 text-xs text-gray-600">Use JazzCash hosted wallet checkout.</div>
            </button>

            <button
              type="button"
              onClick={() => {
                if (paymentMethod !== 'card') {
                  setPaymentMethod('card');
                  setWidgetReady(false);
                  setCheckoutSession(null);
                  setWidgetError('');
                  setStatusNote('');
                }
              }}
              className={`rounded-xl border px-4 py-4 text-left transition ${
                paymentMethod === 'card'
                  ? 'border-emerald-500 bg-emerald-50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <CreditCard className="h-4 w-4 text-emerald-600" />
                Card
              </div>
              <div className="mt-1 text-xs text-gray-600">Use JazzCash hosted card checkout.</div>
            </button>
          </div>

          {!widgetReady ? (
            <button
              type="button"
              onClick={loadHostedCheckout}
              disabled={isPreparing}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isPreparing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {isPreparing ? 'Preparing JazzCash...' : actionLabel}
            </button>
          ) : null}

          {statusNote ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              {statusNote}
            </div>
          ) : null}

          {widgetError ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {widgetError}
            </div>
          ) : null}

          <form
            id="onlineform"
            ref={formRef}
            method="POST"
            action={checkoutSession?.gateway_url || '#'}
            className={`space-y-4 ${widgetReady ? 'block' : 'hidden'}`}
          >
            {paymentMethod === 'card' ? (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 shadow-inner">
                <div className="mb-4 text-xs font-bold uppercase tracking-[0.16em] text-emerald-600 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Secure Card Checkout
                </div>
                <div id="JazzCashFields" className="space-y-3" />
                <div id="JazzCashErrorDiv" className="hidden rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 mt-3 text-sm text-rose-700 font-medium" />
                <div id="JazzCashSuccessDiv" className="hidden rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 mt-3 text-sm text-emerald-700 font-medium" />
                <div id="pp_masterCardResponse" className="hidden mt-3" />
                <div id="stagingForm" className="hidden" />
                <div id="myForm" className="hidden" />
                <div id="responseDiv" className="hidden" />
              </div>
            ) : (
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-6 text-center space-y-4 shadow-sm">
                <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                  <Smartphone className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">JazzCash Mobile Wallet</h4>
                  <p className="text-sm text-gray-600 mt-1 max-w-sm mx-auto">
                    You will be redirected to the secure JazzCash portal to enter your mobile number and authorize the payment.
                  </p>
                </div>
                <div className="border-t border-emerald-100/50 pt-4 flex justify-between items-center text-sm font-medium">
                  <span className="text-gray-500">Payable Amount</span>
                  <span className="font-bold text-gray-900 text-lg">PKR {checkoutContext.amount || '350.00'}</span>
                </div>
              </div>
            )}

            <button
              type="submit"
              id="submitBtnPayAxis"
              className="inline-flex w-full items-center justify-center rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2"
            >
              {paymentMethod === 'card' ? 'Pay by Card' : 'Proceed to JazzCash'}
            </button>
          </form>
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
            Your browser will be redirected back to Journi after JazzCash completes the hosted checkout. If JazzCash returns the result by
            postMessage first, Journi will submit the callback to the backend automatically.
          </div>
        </div>
      </div>
    </div>
  );
}
